import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useTenantQueryKey } from "@/hooks/useTenantContext";
import {
  isSupabaseUnavailableError,
} from "@/lib/supabase-compat";

interface RoleTagging {
  taggable_id: string;
  tags: { name: string } | null;
}

interface PersonTag {
  tag_id: string;
  tags: { id: string; name: string; tag_type: string } | null;
}

// Type for the role tag name (using capitalized names from taggings system)
export type RoleTag = "Member" | "Staff" | "Volunteer" | "Donor";

const SYSTEM_ROLE_TAG_NAMES = ["Member", "Staff", "Volunteer", "Donor"] as const;

/**
 * Idempotently ensures the four system role tags exist for a given organization.
 * New orgs created after the initial migration may lack these tags; this function
 * checks which are missing and inserts only those.
 */
export async function ensureSystemRoleTags(organizationId: string): Promise<void> {
  const { data: existingTags, error: fetchError } = await supabase
    .from("tags")
    .select("name")
    .eq("organization_id", organizationId)
    .eq("tag_type", "role")
    .eq("is_system", true);

  if (fetchError) {
    throw fetchError;
  }

  const existingNames = new Set((existingTags ?? []).map((t) => t.name));
  const missingTags = SYSTEM_ROLE_TAG_NAMES.filter((name) => !existingNames.has(name));

  if (missingTags.length === 0) return;

  const tagsToInsert = missingTags.map((name) => ({
    organization_id: organizationId,
    name,
    tag_type: "role" as const,
    is_system: true,
  }));

  const { error: insertError } = await supabase.from("tags").insert(tagsToInsert);
  if (insertError) {
    throw insertError;
  }
}

// Map from person ID to set of role tags
export type PersonRoleTagMap = Record<string, Set<RoleTag>>;

/**
 * Generic helper to fetch person IDs by system role tag name.
 * Queries the taggings table; returns empty when taggings are unavailable.
 */
function useRolePersonIds(roleName: RoleTag, options?: { enabled?: boolean; cacheScope?: string }) {
  const { organizationId } = useOrganization();
  const isEnabled = options?.enabled ?? true;

  return useQuery({
    queryKey: [`${roleName.toLowerCase()}-person-ids`, organizationId, ...(options?.cacheScope === undefined ? [] : [{ cacheScope: options.cacheScope }])],
    queryFn: async () => {
      if (!organizationId) return new Set<string>();

      try {
        const { data, error } = await supabase
          .from("taggings")
          .select("taggable_id, tags!inner(name)")
          .eq("organization_id", organizationId)
          .eq("taggable_type", "Person")
          .eq("tags.name", roleName)
          .eq("tags.tag_type", "role")
          .eq("tags.is_system", true);

        if (!error) {
          const ids = (data ?? []).map((t: RoleTagging) => t.taggable_id);
          return new Set(ids);
        }

        if (!isSupabaseUnavailableError(error)) {
          console.debug(`Role person IDs query failed for ${roleName} (non-schema):`, error.message);
          return new Set<string>();
        }

        return new Set<string>();
      } catch (err) {
        console.error("Failed to fetch person role tags:", err);
        return new Set<string>();
      }
    },
    enabled: !!organizationId && isEnabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Fetches the set of person IDs tagged with the "Volunteer" system role tag.
 * Used by the Volunteers page to identify volunteers.
 */
export function useVolunteerPersonIds(options?: { enabled?: boolean; cacheScope?: string }) {
  return useRolePersonIds("Volunteer", options);
}

/**
 * Fetches person IDs for one or more canonical system role tags.
 * Role membership is stored in taggings; people.status is only lifecycle state.
 */
export function usePersonIdsByRoleTags(
  roleNames: readonly RoleTag[],
  options?: { enabled?: boolean },
) {
  const { organizationId } = useOrganization();
  const normalizedRoleNames = [...new Set(roleNames)].sort();
  const isEnabled = options?.enabled ?? true;

  return useQuery({
    queryKey: ["role-person-ids", organizationId, normalizedRoleNames.join(",")],
    queryFn: () => fetchPersonIdsByRoleTags(organizationId, normalizedRoleNames),
    enabled: !!organizationId && isEnabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export async function fetchPersonIdsByRoleTags(
  organizationId: string | null | undefined,
  roleNames: readonly RoleTag[],
): Promise<Set<string>> {
  if (!organizationId || roleNames.length === 0) return new Set<string>();

  const { data, error } = await supabase
    .from("taggings")
    .select("taggable_id, tags!inner(name)")
    .eq("organization_id", organizationId)
    .eq("taggable_type", "Person")
    .in("tags.name", roleNames)
    .eq("tags.tag_type", "role")
    .eq("tags.is_system", true);

  if (error) {
    if (!isSupabaseUnavailableError(error)) {
      console.debug("Role person IDs query failed:", error.message);
    }
    return new Set<string>();
  }

  return new Set((data ?? []).map((tagging) => tagging.taggable_id));
}

// PostgREST caps unpaginated result sets at supabase/config.toml's max_rows (1000).
// Orgs with more taggings than that would silently get a truncated page back —
// fine for the fail-soft UI helper above, but a write path needs every row.
const ROLE_TAGGING_PAGE_SIZE = 1000;

/**
 * Strict counterpart to fetchPersonIdsByRoleTags for write paths that must not
 * silently under-report recipients (e.g. fanning out per-person rows). The
 * fail-soft variant above deliberately swallows query errors into an empty
 * set for read-only UI filtering — fine there, but wrong for a write path,
 * where an empty set would look identical to "no one has this role" and
 * silently skip real recipients. This throws instead, and paginates so a
 * result set past PostgREST's max_rows cap doesn't silently truncate.
 */
export async function fetchPersonIdsByRoleTagsStrict(
  organizationId: string,
  roleNames: readonly RoleTag[],
): Promise<Set<string>> {
  if (roleNames.length === 0) return new Set<string>();

  const ids = new Set<string>();
  let from = 0;
  for (;;) {
    const { data, error } = await supabase
      .from("taggings")
      .select("taggable_id, tags!inner(name)")
      .eq("organization_id", organizationId)
      .eq("taggable_type", "Person")
      .in("tags.name", roleNames)
      .eq("tags.tag_type", "role")
      .eq("tags.is_system", true)
      // taggings.tag_id and taggings.organization_id are independent FKs —
      // nothing enforces the referenced tag belongs to the same org as the
      // tagging row, and the taggings INSERT RLS policy only checks
      // organization_id. Without this, a malformed cross-org tagging would
      // count as role membership for the wrong org (write path, so this
      // matters more than the fail-soft read helpers above).
      .eq("tags.organization_id", organizationId)
      .order("id", { ascending: true })
      .range(from, from + ROLE_TAGGING_PAGE_SIZE - 1);

    if (error) throw error;

    const page = data ?? [];
    for (const tagging of page) ids.add(tagging.taggable_id);
    if (page.length < ROLE_TAGGING_PAGE_SIZE) break;
    from += ROLE_TAGGING_PAGE_SIZE;
  }

  return ids;
}

/**
 * Fetches volunteer people with minimal fields needed for the scheduling UI.
 * Instead of loading ALL people (3000+) then filtering client-side, this:
 *  1. Gets volunteer person IDs from taggings
 *  2. Fetches only those people with just the fields the scheduling board needs
 * This eliminates the N+1 problem where usePeople() loads every person with
 * household/ministry relations when only ~10% are volunteers.
 */
export interface VolunteerPerson {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  photo_url: string | null;
  bg_check_status?: "not_started" | "pending" | "cleared" | "flagged" | "expired" | null;
}

export function useVolunteerPeople() {
  const { organizationId } = useOrganization();
  const { data: volunteerIds } = useVolunteerPersonIds();

  return useQuery({
    queryKey: ["volunteer-people", organizationId, volunteerIds ? [...volunteerIds].sort().join(",") : ""],
    queryFn: async (): Promise<VolunteerPerson[]> => {
      if (!organizationId || !volunteerIds || volunteerIds.size === 0) return [];

      const ids = [...volunteerIds];

      // PostgREST .in() supports up to ~1000 IDs comfortably.
      // For very large sets, batch in chunks of 500.
      const BATCH_SIZE = 500;
      const results: VolunteerPerson[] = [];

      for (let i = 0; i < ids.length; i += BATCH_SIZE) {
        const batch = ids.slice(i, i + BATCH_SIZE);
        const { data, error } = await supabase
          .from("people")
          .select("id, first_name, last_name, email, phone, photo_url, bg_check_status")
          .eq("organization_id", organizationId)
          .is("deleted_at", null)
          .in("id", batch)
          .order("last_name", { ascending: true })
          .order("first_name", { ascending: true });

        if (error) {
          if (isSupabaseUnavailableError(error)) return [];
          throw error;
        }
        results.push(...((data || []) as VolunteerPerson[]));
      }

      return results;
    },
    enabled: !!organizationId && !!volunteerIds && volunteerIds.size > 0,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Fetches the set of person IDs tagged with the "Staff" system role tag.
 * Used by HR pages and staff filtering.
 */
export function useStaffPersonIds(options?: { enabled?: boolean; cacheScope?: string }) {
  return useRolePersonIds("Staff", options);
}

/**
 * Fetches the set of person IDs tagged with the "Donor" system role tag.
 * Used for donor filtering and reporting.
 */
export function useDonorPersonIds() {
  return useRolePersonIds("Donor");
}

/**
 * Fetches the set of person IDs tagged with the "Member" system role tag.
 * Used for member filtering and reporting.
 */
export function useMemberPersonIds() {
  return useRolePersonIds("Member");
}

/**
 * Fetches all person-to-role-tags mappings for the entire organization.
 * Returns a map from person ID to Set of role tag names.
 * Queries the taggings table; returns empty when taggings are unavailable.
 */
export function useAllPersonRoleTags() {
  const { organizationId } = useOrganization();
  const tenantQueryKey = useTenantQueryKey();

  return useQuery({
    queryKey: [...tenantQueryKey, "all-person-role-tags", organizationId],
    queryFn: async (): Promise<PersonRoleTagMap> => {
      if (!organizationId) return {};

      try {
        const { data, error} = await supabase
          .from("taggings")
          .select("taggable_id, tags!inner(name)")
          .eq("organization_id", organizationId)
          .eq("taggable_type", "Person")
          .eq("tags.tag_type", "role")
          .eq("tags.is_system", true);

        if (!error) {
          const roleMap: PersonRoleTagMap = {};
          for (const row of data as RoleTagging[]) {
            const personId = row.taggable_id;
            const tagName = row.tags?.name as RoleTag | undefined;
            if (!tagName) continue;
            if (!roleMap[personId]) roleMap[personId] = new Set();
            roleMap[personId].add(tagName);
          }
          return roleMap;
        }

        if (!isSupabaseUnavailableError(error)) {
          console.debug("Role tags query failed (non-schema error), returning empty roles:", error.message);
          return {};
        }

        return {};
      } catch (err) {
        if (err) {
          console.debug("Role tags query error:", err);
        }
        return {};
      }
    },
    enabled: !!organizationId,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Fetches role tag names for a specific person (e.g., ["Volunteer", "Staff", "Member"]).
 * Used by PersonProfile to display roles and conditionally show tabs.
 * Queries the taggings system exclusively (boolean columns were removed in migration 20260410000031).
 */
export function usePersonRoleTags(personId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["person-role-tags", organizationId, personId],
    queryFn: async () => {
      if (!organizationId || !personId) return [];

      try {
        const { data, error } = await supabase
          .from("taggings")
          .select("tag_id, tags!inner(id, name, tag_type)")
          .eq("organization_id", organizationId)
          .eq("taggable_type", "Person")
          .eq("taggable_id", personId)
          .eq("tags.tag_type", "role")
          .eq("tags.is_system", true);

        if (!error) {
          return (
            (data as PersonTag[])
              .map((t) => t.tags?.name)
              .filter((name): name is string => !!name)
          );
        }

        if (!isSupabaseUnavailableError(error)) {
          console.debug("Person role tags query failed (non-schema error), returning empty:", error.message);
          return [];
        }

        return [];
      } catch (err) {
        console.error("Failed to fetch available role tags:", err);
        return [];
      }
    },
    enabled: !!organizationId && !!personId,
  });
}

interface SyncRoleTagsInput {
  personId: string;
  roles: {
    isMember: boolean;
    isVolunteer: boolean;
    isStaff: boolean;
    isDonor: boolean;
  };
}

/**
 * Mutation to sync a person's system role tags (Member, Volunteer, Staff, Donor).
 * Replaces the dropped boolean columns on the people table.
 */
export function useSyncPersonRoleTags() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({ personId, roles }: SyncRoleTagsInput) => {
      if (!organizationId) throw new Error("No organization selected");

      // Get system role tags for this org
      let roleTags: Array<{ id: string; name: string }>;
      const { data: fetchedTags, error: tagError } = await supabase
        .from("tags")
        .select("id, name")
        .eq("organization_id", organizationId)
        .eq("tag_type", "role")
        .eq("is_system", true);

      if (tagError) throw tagError;

      if (!fetchedTags || fetchedTags.length === 0) {
        // Auto-seed system role tags for this org (migration may not have run for new orgs)
        await ensureSystemRoleTags(organizationId);
        // Re-query after seeding
        const { data: requeriedTags, error: reqError } = await supabase
          .from("tags")
          .select("id, name")
          .eq("organization_id", organizationId)
          .eq("tag_type", "role")
          .eq("is_system", true);
        if (reqError || !requeriedTags || requeriedTags.length === 0) return;
        roleTags = requeriedTags;
      } else {
        roleTags = fetchedTags;
      }

      const roleTagMap = new Map<string, string>(
        roleTags.map((t) => [t.name, t.id])
      );

      const desiredRoles: string[] = [];
      if (roles.isMember) desiredRoles.push("Member");
      if (roles.isVolunteer) desiredRoles.push("Volunteer");
      if (roles.isStaff) desiredRoles.push("Staff");
      if (roles.isDonor) desiredRoles.push("Donor");

      // Delete all existing role taggings for this person
      const allRoleTagIds = Array.from(roleTagMap.values());
      if (allRoleTagIds.length > 0) {
        const { error: deleteError } = await supabase
          .from("taggings")
          .delete()
          .eq("organization_id", organizationId)
          .eq("taggable_type", "Person")
          .eq("taggable_id", personId)
          .in("tag_id", allRoleTagIds);

        if (deleteError) throw deleteError;
      }

      // Insert desired role taggings
      const taggingsToInsert = desiredRoles
        .map((role) => {
          const tagId = roleTagMap.get(role);
          if (!tagId) return null;
          return {
            organization_id: organizationId,
            tag_id: tagId,
            taggable_type: "Person",
            taggable_id: personId,
          };
        })
        .filter(Boolean);

      if (taggingsToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from("taggings")
          .insert(taggingsToInsert);

        if (insertError) throw insertError;
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["person-role-tags", organizationId, variables.personId],
      });
      queryClient.invalidateQueries({
        queryKey: ["all-person-role-tags", organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["member-person-ids", organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["staff-person-ids", organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["donor-person-ids", organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["volunteer-person-ids", organizationId],
      });
    },
  });
}
